import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi, API } from "../lib/api";
import { toast } from "sonner";
import { FileText, Eraser, CheckCircle2, PenLine, ShieldCheck } from "lucide-react";

const fmtDt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#0a0a0a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk.current) { hasInk.current = true; onChange(true); }
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasInk.current = false;
    onChange(false);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        data-testid="sign-canvas"
        className="w-full h-44 bg-white border-2 border-dashed border-[#71717A] touch-none cursor-crosshair rounded-none"
        style={{ touchAction: "none" }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div className="flex justify-between items-center mt-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Sign above with your finger or mouse</div>
        <button data-testid="sign-clear" onClick={clear}
          className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] hover:text-[#F5F2EB] inline-flex items-center gap-1">
          <Eraser size={12}/> Clear
        </button>
      </div>
    </div>
  );
}

// Exposed so the submit handler can pull the PNG out of the canvas
const getCanvasData = () => {
  const c = document.querySelector('[data-testid="sign-canvas"]');
  return c ? c.toDataURL("image/png") : null;
};

export default function RentalSign() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [hasInk, setHasInk] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get(`/rental-doc/${token}`)
      .then(r => setDoc(r.data))
      .catch(e => setErr(e?.response?.status === 404 ? "This signing link is invalid or has expired." : "Could not load the agreement."));
  }, [token]);

  const pdfUrl = `${API}/rental-doc/${token}/agreement.pdf`;

  const submit = async () => {
    if (!hasInk) { toast.error("Please draw your signature first"); return; }
    if (!agree) { toast.error("Please check the agreement box"); return; }
    const data = getCanvasData();
    if (!data) return;
    setBusy(true);
    try {
      await publicApi.post(`/rental-doc/${token}/sign`, { signature_base64: data });
      setDone(true);
      setDoc(prev => ({ ...prev, signed: true }));
      toast.success("Agreement signed — thank you!");
    } catch (e) {
      if (e?.response?.status === 409) { setDone(true); setDoc(prev => ({ ...prev, signed: true })); }
      else toast.error(e?.response?.data?.detail || "Signing failed — please try again");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="public-light min-h-screen bg-[#050505] text-[#F5F2EB] flex items-center justify-center p-6" data-testid="rental-sign-error">
        <div className="max-w-md text-center">
          <div className="font-serif text-2xl mb-2">Delux Limo Trans Group</div>
          <p className="text-sm text-[#71717A]">{err}</p>
          <p className="text-xs text-[#71717A] mt-4">Questions? Call (414) 333-6275.</p>
        </div>
      </div>
    );
  }
  if (!doc) {
    return <div className="public-light min-h-screen bg-[#050505] text-[#F5F2EB] flex items-center justify-center">Loading…</div>;
  }

  const signedView = done || doc.signed;

  return (
    <div className="public-light min-h-screen bg-[#050505] text-[#F5F2EB]" data-testid="rental-sign-shell">
      <div className="max-w-xl mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <div className="font-serif text-2xl">Delux Limo Trans Group</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A] mt-1">Car Rental Agreement · {doc.rental_code}</div>
        </div>

        {/* Summary card */}
        <div className="border border-white/10 bg-[#0A0A0A] p-6 space-y-3 mb-6" data-testid="rental-sign-summary">
          <div className="flex justify-between text-sm"><span className="text-[#71717A]">Renter</span><span>{doc.renter_name}</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#71717A]">Vehicle</span><span>{doc.vehicle_name}</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#71717A]">Pickup</span><span className="text-right">{fmtDt(doc.pickup_datetime)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-[#71717A]">Return</span><span className="text-right">{fmtDt(doc.return_datetime)}</span></div>
          <div className="flex justify-between text-sm border-t border-white/10 pt-3">
            <span className="text-[#71717A]">Total ({doc.days} day{doc.days === 1 ? "" : "s"})</span>
            <span className="text-[#D4AF37] font-bold">${Number(doc.total).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs"><span className="text-[#71717A]">Refundable deposit</span><span>${Number(doc.security_deposit).toFixed(2)}</span></div>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="rental-sign-view-pdf"
             className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline">
            <FileText size={13}/> View full agreement (PDF)
          </a>
        </div>

        {/* Optional third-party insurance the renter can buy directly */}
        {(doc.insurance_partners || []).length > 0 && (
          <div className="border border-white/10 bg-[#0A0A0A] p-6 mb-6" data-testid="insurance-partners-card">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1 inline-flex items-center gap-2">
              <ShieldCheck size={12}/> Protect your rental
            </div>
            <p className="text-xs text-[#71717A] mb-4">
              Optional damage &amp; liability coverage from our partners — purchased directly with them,
              separate from your rental charges here.
            </p>
            <div className="space-y-2">
              {doc.insurance_partners.map(p => (
                <a key={p.key} href={p.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`insurance-partner-${p.key}`}
                  onClick={() => { publicApi.post(`/rental-doc/${token}/insurance-click`, { provider: p.key }).catch(() => {}); }}
                  className="flex items-center gap-4 border border-white/10 hover:border-[#D4AF37]/60 p-3.5 transition-colors group">
                  <span className="w-9 h-9 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] font-bold flex-shrink-0">
                    {{ rental_cover: "RC", bonzah: "BZ", allianz: "AL" }[p.key]}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-[#F5F2EB]">{p.name}</span>
                    <span className="block text-[11px] text-[#71717A] leading-snug">{p.blurb}</span>
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] whitespace-nowrap group-hover:underline">
                    Get a quote →
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}

        {signedView ? (
          <div className="border border-emerald-400/30 bg-emerald-400/5 p-8 text-center" data-testid="rental-sign-done">
            <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-3"/>
            <div className="font-serif text-xl mb-1">Agreement signed</div>
            <p className="text-xs text-[#71717A]">Your e-signature has been recorded and added to the rental agreement.</p>
            <div className="mt-5 flex flex-col gap-3 items-center">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                 className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline inline-flex items-center gap-2">
                <FileText size={13}/> Download signed agreement
              </a>
              {doc.payment_status !== "paid" && doc.square_payment_url && (
                <a href={doc.square_payment_url} target="_blank" rel="noopener noreferrer" data-testid="rental-sign-pay"
                   className="btn-gold text-[11px] uppercase tracking-[0.22em] px-8 py-3">
                  Pay now — ${Number(doc.total).toFixed(2)}
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="rental-sign-pad-card">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-4 inline-flex items-center gap-2">
              <PenLine size={12}/> Your signature
            </div>
            <SignaturePad onChange={setHasInk}/>
            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <input type="checkbox" data-testid="sign-agree-checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
                     className="w-4 h-4 mt-0.5 accent-[#D4AF37]"/>
              <span className="text-xs text-[#A1A1AA] leading-relaxed">
                I, <span className="text-[#F5F2EB]">{doc.renter_name}</span>, have read and agree to the rental
                agreement terms &amp; conditions (see the full PDF above). I understand my electronic signature
                is legally binding.
              </span>
            </label>
            <button data-testid="sign-submit" onClick={submit} disabled={busy}
              className="btn-gold w-full justify-center text-[11px] uppercase tracking-[0.22em] py-3.5 mt-5 disabled:opacity-50">
              {busy ? "Signing…" : "Agree & sign"}
            </button>
          </div>
        )}

        <p className="text-center text-[10px] text-[#71717A] mt-8">
          Questions? Call (414) 333-6275 · reservations@deluxlimotransgroup.com
        </p>
      </div>
    </div>
  );
}
